/*
 *  -----------------------------------------------------------------------
 *    <copyright file="user-profile.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import SmStudyMembership from '@pages/uam/sm-study-membership';
import { useAuthorization, useBoolean, useTranslation } from 'hooks';
import { UserFields, OrganisationMemberFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION } from 'queries/uam';
import { Authentication } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import NotificationService, { ShowError } from 'utils/notification';
import UpdateAccountDialog from 'views/components/account/update-account-dialog';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import MyAccount from 'views/pages/uam/my-account';
import StudyMembership from 'views/pages/uam/study-membership';

const UserProfile = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const openDialog = useBoolean(false);
    const [dialogType, setDialogType] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');

    const { data, loading, error } = useQuery(GET_ORGANISATION);
    const organisationMember = data?.organisation?.members.find(
        (member: OrganisationMemberFields) => member.userId === user.id,
    );
    const isSso =
        data?.organisation?.security?.authenticationMethod === Authentication.SSO &&
        user.systemAdminLevel === Constants.SystemAdminLevel.None;

    const onDialogOpen = (title: string, type: string) => {
        setDialogTitle(title);
        setDialogType(type);
        openDialog.setTrue();
    };

    const menuActions = [
        {
            label: 'uam:editName',
            icon: ['fal', 'edit'],
            onClick: () => onDialogOpen(t('uam:editName'), 'name'),
            enabled: !isSso,
        },
        {
            label: 'uam:editCellMobileNumber',
            icon: ['fal', 'edit'],
            onClick: () => onDialogOpen(t('uam:editCellMobileNumber'), 'phone'),
            enabled: !isSso,
        },
        {
            label: 'uam:editEmail',
            icon: ['fal', 'edit'],
            onClick: () => onDialogOpen(t('uam:editEmail'), 'email'),
            enabled: !isSso,
        },
        {
            label: 'uam:editPassword',
            icon: ['fal', 'edit'],
            onClick: () => onDialogOpen(t('uam:editPassword'), 'password'),
            enabled: !isSso,
        },
    ];

    const errorNotification = (errorData?: { error: boolean; errorMessage: string }) => {
        ShowError(errorData?.errorMessage ?? t('common:errorMessage'));
    };

    const onUpdateAccountDialogClose = (action: string, errorMessage?: string) => {
        if (action === 'error') {
            errorNotification({ error: true, errorMessage });
        } else {
            if (action === 'update') NotificationService.showNotification(t('uam:accountHasBeenSuccessfullyUpdated'));
            openDialog.setFalse();
        }
    };

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <div className="account-profile-wrap">
            <UpdateAccountDialog
                open={openDialog.value}
                dialogTitle={dialogTitle}
                onClose={onUpdateAccountDialogClose}
                onAccountUpdate={(updatedUser: UserFields) => {
                    user.setUser({
                        ...user,
                        ...updatedUser,
                    });
                }}
                user={user}
                dialogType={dialogType}
            />
            <PageTitle text={t('uam:accountProfile')} />
            <ItemWrapper title={t('uam:myAccount')} customRef={null} actions={menuActions} hideItemNumber>
                <MyAccount user={user} organisationMember={organisationMember} />
            </ItemWrapper>
            {user?.systemAdminLevel <= 0 && (
                <StudyMembership
                    user={user}
                    organisationMember={organisationMember}
                    errorNotification={errorNotification}
                />
            )}{' '}
            {user && user.systemAdminLevel <= 0 && (
                <SmStudyMembership
                    user={user}
                    organisationMember={organisationMember}
                    errorNotification={errorNotification}
                />
            )}
        </div>
    );
};

export default UserProfile;
